import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { AccountInfo, InteractionRequiredAuthError, InteractionStatus, BrowserAuthError } from '@azure/msal-browser';
import { loginRequest } from './authConfig';
import { authLogger } from '../utils/logger';
import { detectTeamsContext, getTeamsSsoToken } from './teamsContext';
import { APIStorage } from '../services/apiStorage';

import { AUTH_SESSION_EXPIRED_EVENT } from './authEvents';

export type UserRole = 'employee' | 'manager' | 'hr';

interface UserInfo {
  id: string;
  name: string;
  email: string;
  role: UserRole;
}

export interface MfaState {
  mfaEnabled: boolean;
  mfaVerified: boolean;
  entraMfaVerified: boolean;
  graceDaysRemaining: number;
  graceExpired: boolean;
}

export type AuthMode = 'browser' | 'teams';

interface AuthContextType {
  user: UserInfo | null;
  account: AccountInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHR: boolean;
  isManager: boolean;
  authMode: AuthMode;
  mfaState: MfaState;
  login: () => Promise<void>;
  logout: () => void;
  getAccessToken: (forceRefresh?: boolean) => Promise<string>;
  updateManagerStatus: (isManager: boolean) => void;
  updateMfaState: (state: Partial<MfaState>) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { instance, accounts, inProgress } = useMsal();
  const msalIsAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isCheckingRole, setIsCheckingRole] = useState(false);
  const hasCheckedRole = useRef(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('browser');
  const [authModeResolved, setAuthModeResolved] = useState(false);
  // Teams-SSO setzt User direkt aus dem Backend-JWT (kein MSAL-Account)
  const [teamsAuthed, setTeamsAuthed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void detectTeamsContext().then((inTeams) => {
      if (cancelled) return;
      setAuthMode(inTeams ? 'teams' : 'browser');
      setAuthModeResolved(true);

      // Teams-Reload: gültigen JWT aus localStorage übernehmen, ohne neuen
      // SSO-Roundtrip — User-State aus dem JWT-Payload wiederherstellen.
      if (inTeams) {
        const storage = new APIStorage();
        if (!storage.hasValidToken()) return;
        const payload = storage.getTokenPayload();
        if (!payload) return;

        const isHRClaim = payload.isHR === true;
        const isManagerClaim = payload.isManager === true;
        setUser({
          id: typeof payload.id === 'string' ? payload.id : '',
          name: typeof payload.name === 'string' ? payload.name : 'Unbekannt',
          email: typeof payload.email === 'string' ? payload.email : '',
          role: isHRClaim ? 'hr' : (isManagerClaim ? 'manager' : 'employee'),
        });
        setMfaState({
          mfaEnabled: payload.mfaEnabled === true,
          mfaVerified: payload.mfaVerified === true,
          entraMfaVerified: payload.entraMfaVerified === true,
          graceDaysRemaining: 0,
          graceExpired: false,
        });
        setTeamsAuthed(true);
        hasCheckedRole.current = true;
      }
    });
    return () => { cancelled = true; };
  }, []);
  const [mfaState, setMfaState] = useState<MfaState>({
    mfaEnabled: false,
    mfaVerified: false,
    entraMfaVerified: false,
    graceDaysRemaining: 0,
    graceExpired: false,
  });

  // Effective auth: in Teams reicht der Backend-JWT (teamsAuthed); sonst MSAL.
  const isAuthenticated = (authMode === 'teams' ? teamsAuthed : msalIsAuthenticated) && !sessionExpired;

  // Listen for session-expired events (dispatched by apiStorage on 401 or getAccessToken on InteractionRequired)
  useEffect(() => {
    const handleSessionExpired = () => {
      authLogger.warn('Sitzung abgelaufen — Auth-State wird zurückgesetzt');
      setSessionExpired(true);
      setUser(null);
      hasCheckedRole.current = false;
      setMfaState({
        mfaEnabled: false,
        mfaVerified: false,
        entraMfaVerified: false,
        graceDaysRemaining: 0,
        graceExpired: false,
      });
    };
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  const getAccessToken = useCallback(async (forceRefresh = false): Promise<string> => {
    if (authMode === 'teams') {
      // Teams cached den SSO-Token intern und liefert bei Bedarf einen frischen.
      // forceRefresh wird ignoriert (Teams-SDK hat keine entsprechende Option),
      // ist aber auch nicht nötig — der SDK-interne Cache prüft Ablauf selbst.
      try {
        return await getTeamsSsoToken();
      } catch (error) {
        authLogger.warn('Teams-SSO-Token konnte nicht erneuert werden', error);
        window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
        throw error;
      }
    }

    if (!accounts[0]) {
      throw new Error('Kein authentifizierter Benutzer');
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
        forceRefresh,
      });
      return response.accessToken;
    } catch (error) {
      // Silent token refresh failed — check if interactive re-auth is needed
      if (error instanceof InteractionRequiredAuthError) {
        authLogger.warn('Token-Erneuerung fehlgeschlagen — erneute Anmeldung erforderlich');
        window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
      } else if (error instanceof BrowserAuthError) {
        authLogger.warn('MSAL-Fehler — erneute Anmeldung erforderlich');
        // Clear corrupted MSAL state so next login starts clean
        try {
          await instance.clearCache();
        } catch { /* best effort */ }
        window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
      } else {
        authLogger.error('Token-Abruf fehlgeschlagen', error);
      }
      throw error;
    }
  }, [instance, accounts, authMode]);

  const checkGroupMembership = useCallback(async (accessToken: string): Promise<{ isHR: boolean }> => {
    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me/memberOf', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        authLogger.warn('Fehler beim Prüfen der Gruppenmitgliedschaft', { status: response.status });
        return { isHR: false };
      }

      const data = await response.json();
      const groups = data.value || [];

      const hrGroupId = import.meta.env.VITE_HR_GROUP_ID;

      const isHR = hrGroupId
        ? groups.some((group: { id: string }) => group.id === hrGroupId)
        : false;

      // Manager-Status wird über DB-Zuordnung bestimmt (Backend JWT)
      return { isHR };
    } catch (error) {
      authLogger.error('Fehler beim Prüfen der Gruppenmitgliedschaft', error);
      return { isHR: false };
    }
  }, []);

  useEffect(() => {
    const initializeUser = async () => {
      // Teams-Mode setzt User direkt aus dem Backend-JWT in login() — kein Graph-Call
      if (authMode === 'teams') return;
      if (!isAuthenticated || !accounts[0] || hasCheckedRole.current) {
        return;
      }

      hasCheckedRole.current = true;
      setIsCheckingRole(true);

      try {
        const accessToken = await getAccessToken();
        const { isHR } = await checkGroupMembership(accessToken);

        // Initial: HR aus Azure AD Gruppe, Manager wird nach Backend-Login aktualisiert
        const role: UserRole = isHR ? 'hr' : 'employee';

        authLogger.info('Benutzer initialisiert', {
          userId: accounts[0].localAccountId,
          role,
          isHR,
        });

        setUser({
          id: accounts[0].localAccountId,
          name: accounts[0].name || 'Unbekannt',
          email: accounts[0].username || '',
          role,
        });
      } catch (error) {
        authLogger.error('Fehler beim Initialisieren des Benutzers', error);
        // If token can't be renewed, session-expired was already dispatched
        // → login page will be shown, no need for fallback user
        if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
          return;
        }
        // For network/other errors, set fallback user so the app can try loading
        setUser({
          id: accounts[0].localAccountId,
          name: accounts[0].name || 'Unbekannt',
          email: accounts[0].username || '',
          role: 'employee',
        });
      } finally {
        setIsCheckingRole(false);
      }
    };

    void initializeUser();
  }, [isAuthenticated, accounts, getAccessToken, checkGroupMembership, authMode]);

  const updateMfaState = useCallback((partial: Partial<MfaState>) => {
    setMfaState((prev) => ({ ...prev, ...partial }));
  }, []);

  // Manager-Status nach Backend-Login aktualisieren (wird von APIDataLoader aufgerufen)
  const updateManagerStatus = useCallback((isManager: boolean) => {
    setUser((prev) => {
      if (!prev) return prev;
      // HR bleibt HR, nur employee → manager upgraden
      if (prev.role === 'hr') return prev;
      const newRole: UserRole = isManager ? 'manager' : 'employee';
      if (prev.role === newRole) return prev;
      authLogger.info('Manager-Status aktualisiert', { isManager, newRole });
      return { ...prev, role: newRole };
    });
  }, []);

  const login = useCallback(async () => {
    authLogger.info('Login gestartet', { authMode });

    if (authMode === 'teams') {
      try {
        const ssoToken = await getTeamsSsoToken();
        const storage = new APIStorage();
        const response = await storage.loginSso(ssoToken);
        setUser({
          id: response.user.id,
          name: response.user.name,
          email: response.user.email,
          role: response.user.isHR ? 'hr' : (response.user.isManager ? 'manager' : 'employee'),
        });
        setMfaState({
          mfaEnabled: response.user.mfaEnabled ?? false,
          mfaVerified: !(response.user.mfaEnabled ?? false),
          entraMfaVerified: response.entraMfaVerified ?? true,
          graceDaysRemaining: 0,
          graceExpired: false,
        });
        setSessionExpired(false);
        setTeamsAuthed(true);
        hasCheckedRole.current = true;
        authLogger.info('Teams-SSO-Login erfolgreich');
      } catch (error) {
        authLogger.error('Teams-SSO-Login fehlgeschlagen', error);
        throw error;
      }
      return;
    }

    try {
      await instance.loginPopup(loginRequest);
      setSessionExpired(false);
      hasCheckedRole.current = false;
      authLogger.info('Login erfolgreich');
    } catch (error) {
      authLogger.error('Anmeldung fehlgeschlagen', error);
      // If MSAL state is corrupted, clear cache so next attempt starts clean
      if (error instanceof BrowserAuthError) {
        try {
          await instance.clearCache();
        } catch { /* best effort */ }
      }
      throw error;
    }
  }, [instance, authMode]);

  const logout = useCallback(() => {
    authLogger.info('Logout', { authMode });
    setUser(null);
    hasCheckedRole.current = false;
    setSessionExpired(false);

    if (authMode === 'teams') {
      // In Teams keine MSAL-Session — nur lokalen Backend-JWT verwerfen.
      // Teams-Session selbst liegt außerhalb unserer Kontrolle.
      const storage = new APIStorage();
      storage.clearToken();
      setTeamsAuthed(false);
      return;
    }

    instance.logoutPopup({
      postLogoutRedirectUri: window.location.origin,
    }).catch((err: unknown) => authLogger.error('Logout fehlgeschlagen', err));
  }, [instance, authMode]);

  const msalLoading = inProgress !== InteractionStatus.None;
  const isLoading = !authModeResolved
    || isCheckingRole
    || (authMode === 'browser' && msalLoading);

  return (
    <AuthContext.Provider
      value={{
        user,
        account: accounts[0] || null,
        isAuthenticated,
        isLoading,
        isHR: user?.role === 'hr',
        isManager: user?.role === 'manager' || user?.role === 'hr', // HR hat auch Manager-Rechte
        authMode,
        mfaState,
        login,
        logout,
        getAccessToken,
        updateManagerStatus,
        updateMfaState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth muss innerhalb eines AuthProviders verwendet werden');
  }
  return context;
}
